import React, { useEffect, useState } from 'react';
import { Box, Paper, Typography, Stack, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, InputLabel, FormControl, Alert, Link, Table, TableHead, TableRow, TableCell, TableBody, InputAdornment, IconButton, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Visibility, VisibilityOff, ExpandMore } from '@mui/icons-material';
import apiClient from '../../api';

// Samsara-only scaffold. UI only; backend wiring will be added later.

export default function IntegrationsManagement() {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ label: 'Samsara ' + new Date().getFullYear(), environment: 'production', accessToken: '', organizationId: '' });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showToken, setShowToken] = useState(false);
  const [testResponse, setTestResponse] = useState(null);
  const [testing, setTesting] = useState(false);
  const [editingConnection, setEditingConnection] = useState(null);
  const [editForm, setEditForm] = useState({ label: '', environment: 'production', accessToken: '', organizationId: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [connectionToDelete, setConnectionToDelete] = useState(null);
  const [testingConnection, setTestingConnection] = useState(null);
  const [saving, setSaving] = useState(false);
  const [togglingConnection, setTogglingConnection] = useState(null);

  const openAddConnection = () => {
    setForm({ label: 'Samsara ' + new Date().getFullYear(), environment: 'production', accessToken: '', organizationId: '' });
    setError('');
    setSuccess('');
    setTestResponse(null);
    setEditingConnection(null);
    setDialogOpen(true);
  };

  const openEditConnection = (connection) => {
    setEditForm({
      label: connection.label,
      environment: connection.environment,
      accessToken: '', // Don't pre-fill token for security
      organizationId: connection.organizationId || ''
    });
    setError('');
    setSuccess('');
    setTestResponse(null);
    setEditingConnection(connection);
    setDialogOpen(true);
  };

  const loadConnections = async () => {
    setLoading(true);
    try {
      const res = await apiClient.get('/samsara/connections');
      console.log('Loading connections:', res.data.connections?.length || 0, 'connections');
      console.log('Connection data:', res.data.connections);
      setConnections(res.data.connections || []);
    } catch (err) {
      console.error('Error loading connections:', err);
      setConnections([]);
    } finally {
      setLoading(false);
    }
  };

  // Clean up any leftover test connections
  const cleanupTestConnections = async () => {
    try {
      const response = await apiClient.get('/samsara/connections');
      const testConnections = (response.data.connections || []).filter(conn => 
        conn.label.startsWith('test-')
      );
      
      for (const testConn of testConnections) {
        try {
          await apiClient.delete(`/samsara/connections/${testConn._id}`);
          console.log(`[Cleanup] Removed test connection: ${testConn.label}`);
        } catch (deleteErr) {
          console.warn(`[Cleanup] Failed to delete test connection ${testConn.label}:`, deleteErr);
        }
      }
      
      if (testConnections.length > 0) {
        console.log(`[Cleanup] Cleaned up ${testConnections.length} test connections`);
        // Refresh the connections list after cleanup
        await loadConnections();
      }
    } catch (err) {
      console.error('Failed to cleanup test connections:', err);
    }
  };

  useEffect(() => { 
    loadConnections();
    cleanupTestConnections(); // Clean up any leftover test connections on component mount
  }, []);

  const maskToken = (token) => {
    if (!token || token.length < 8) return '••••••••';
    return '••••••••' + token.slice(-4);
  };

  const getCurrentForm = () => editingConnection ? editForm : form;

  const openDeleteDialog = (connection) => {
    setConnectionToDelete(connection);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!connectionToDelete) return;
    
    try {
      await apiClient.delete(`/samsara/connections/${connectionToDelete._id}`);
      loadConnections();
      setDeleteDialogOpen(false);
      setConnectionToDelete(null);
    } catch (err) {
      setError('Failed to delete connection');
    }
  };

  const testExistingConnection = async (connection) => {
    setTestingConnection(connection._id);
    try {
      const response = await apiClient.post(`/samsara/connections/${connection._id}/test`);
      setSuccess(`Connection test successful: ${response.data.org?.name || 'Connected'}`);
      loadConnections();
    } catch (err) {
      setError(`Connection test failed: ${err?.response?.data?.message || err.message}`);
    } finally {
      setTestingConnection(null);
    }
  };

  const toggleConnection = async (connection) => {
    setTogglingConnection(connection._id);
    try {
      const response = await apiClient.patch(`/samsara/connections/${connection._id}/toggle`);
      console.log('Toggle response:', response.data);
      const newStatus = response.data.connection.active;
      const action = newStatus ? 'activated' : 'deactivated';
      setSuccess(`Connection "${connection.label}" ${action} successfully`);
      await loadConnections(); // Ensure we wait for the refresh
    } catch (err) {
      setError(`Failed to toggle connection: ${err?.response?.data?.message || err.message}`);
    } finally {
      setTogglingConnection(null);
    }
  };

  const testConnection = async () => {
    const currentForm = getCurrentForm();
    if (!currentForm.label || !currentForm.accessToken) {
      setError('Label and Access Token are required.');
      return;
    }
    
    setTesting(true);
    setTestResponse(null);
    setError('');
    
    let connectionId = null;
    
    try {
      // First create a temporary connection to test the token
      const createResponse = await apiClient.post('/samsara/connections', {
        label: `test-${Date.now()}`, // Use unique label for test
        environment: currentForm.environment,
        accessToken: currentForm.accessToken,
        organizationId: currentForm.organizationId
      });
      
      connectionId = createResponse.data.connection._id;
      
      // Now test the actual connection
      const testResponse = await apiClient.post(`/samsara/connections/${connectionId}/test`);
      
      setTestResponse({
        success: testResponse.data.ok,
        status: testResponse.status,
        data: testResponse.data,
        message: testResponse.data.ok ? 
          `✅ Connection successful! Organization: ${testResponse.data.org?.data?.name || testResponse.data.org?.name || 'Unknown'}` :
          `❌ Connection failed: ${testResponse.data.error}`
      });
    } catch (err) {
      setTestResponse({
        success: false,
        status: err?.response?.status || 'No Status',
        error: err?.response?.data?.error || err?.response?.data?.message || err.message || 'Unknown error',
        message: `❌ Test failed: ${err?.response?.data?.error || err?.response?.data?.message || err.message}`,
        fullError: err
      });
    } finally {
      // Always clean up the test connection, regardless of success or failure
      if (connectionId) {
        try {
          await apiClient.delete(`/samsara/connections/${connectionId}`);
          console.log(`[Test] Cleaned up test connection: ${connectionId}`);
        } catch (deleteErr) {
          console.warn('Failed to clean up test connection:', deleteErr);
        }
      }
      setTesting(false);
    }
  };

  const saveConnection = async () => {
    if (saving) return; // Prevent multiple saves
    
    const currentForm = getCurrentForm();
    if (!currentForm.label || !currentForm.accessToken) {
      setError('Label and Access Token are required.');
      return;
    }
    
    // Check if we have a test result and it failed
    if (testResponse && !testResponse.success) {
      setError('Cannot save connection: Test failed. Please fix the access token and test again.');
      return;
    }
    
    // Require successful test before saving new connections
    if (!editingConnection && (!testResponse || !testResponse.success)) {
      setError('Please test the connection successfully before saving.');
      return;
    }
    
    setSaving(true);
    setError('');
    setSuccess('');
    
    try {
      if (editingConnection) {
        // Update existing connection
        await apiClient.put(`/samsara/connections/${editingConnection._id}`, {
          label: currentForm.label,
          environment: currentForm.environment,
          accessToken: currentForm.accessToken,
          organizationId: currentForm.organizationId
        });
        setSuccess('Connection updated successfully');
      } else {
        // Create new connection
        await apiClient.post('/samsara/connections', {
          label: currentForm.label,
          environment: currentForm.environment,
          accessToken: currentForm.accessToken,
          organizationId: currentForm.organizationId
        });
        setSuccess('Connection created successfully');
      }
      setDialogOpen(false);
      await loadConnections();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save connection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Paper sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6">Samsara Integration</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={openAddConnection}>Add Access Token</Button>
          <Button component={Link} href="https://developers.samsara.com/reference/overview" target="_blank" rel="noopener" sx={{ ml: 1 }}>
            Docs
          </Button>
        </Stack>
      </Stack>

      {error && (
        <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>Label</TableCell>
            <TableCell>Provider</TableCell>
            <TableCell>Environment</TableCell>
            <TableCell>Token</TableCell>
            <TableCell>Status</TableCell>
            <TableCell>Organization</TableCell>
            <TableCell align="right">Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {connections.length === 0 ? (
            <TableRow>
              <TableCell colSpan={7} sx={{ color: 'text.secondary' }}>
                No Samsara connections yet. Click "Add Access Token" to create one.
              </TableCell>
            </TableRow>
          ) : (
            connections.map(conn => (
              <TableRow key={conn._id} hover>
                <TableCell>{conn.label}</TableCell>
                <TableCell>Samsara</TableCell>
                <TableCell>{conn.environment}</TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                    {maskToken(conn.accessToken || 'samsara_api_••••••••••••••••••••••••••••••••••••••••')}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip 
                    size="small" 
                    label={conn.active ? 'Active' : 'Inactive'} 
                    color={conn.active ? 'success' : 'default'} 
                  />
                </TableCell>
                <TableCell>{conn.organizationId ? `Org ${conn.organizationId}` : '-'}</TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={1} justifyContent="flex-end">
                    <Button size="small" variant="outlined" onClick={() => openEditConnection(conn)}>
                      Edit
                    </Button>
                    <Button 
                      size="small" 
                      variant="outlined" 
                      onClick={() => testExistingConnection(conn)}
                      disabled={testingConnection === conn._id}
                    >
                      {testingConnection === conn._id ? 'Testing...' : 'Test'}
                    </Button>
                    <Button 
                      size="small" 
                      variant={conn.active ? "outlined" : "contained"}
                      color={conn.active ? "warning" : "success"}
                      onClick={() => toggleConnection(conn)}
                      disabled={togglingConnection === conn._id}
                    >
                      {togglingConnection === conn._id ? 'Toggling...' : (conn.active ? 'Deactivate' : 'Activate')}
                    </Button>
                    <Button 
                      size="small" 
                      color="error" 
                      onClick={() => openDeleteDialog(conn)}
                    >
                      Remove
                    </Button>
                  </Stack>
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>{editingConnection ? 'Edit Samsara Connection' : 'Add Samsara Connection'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {error ? <Alert severity="error" onClose={() => setError('')}>{error}</Alert> : null}
            {success ? <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert> : null}
            <TextField 
              label="Label" 
              value={getCurrentForm().label} 
              onChange={(e) => {
                if (editingConnection) {
                  setEditForm({ ...editForm, label: e.target.value });
                } else {
                  setForm({ ...form, label: e.target.value });
                }
              }} 
              fullWidth 
            />
            <FormControl fullWidth>
              <InputLabel id="env-label">Environment</InputLabel>
              <Select 
                labelId="env-label" 
                label="Environment" 
                value={getCurrentForm().environment} 
                onChange={(e) => {
                  if (editingConnection) {
                    setEditForm({ ...editForm, environment: e.target.value });
                  } else {
                    setForm({ ...form, environment: e.target.value });
                  }
                }}
              >
                <MenuItem value="production">Production</MenuItem>
                <MenuItem value="sandbox">Sandbox</MenuItem>
              </Select>
            </FormControl>
            <TextField 
              label="Organization ID (optional)" 
              value={getCurrentForm().organizationId} 
              onChange={(e) => {
                if (editingConnection) {
                  setEditForm({ ...editForm, organizationId: e.target.value });
                } else {
                  setForm({ ...form, organizationId: e.target.value });
                }
              }} 
              fullWidth 
            />
            <TextField 
              label={editingConnection ? "New Access Token (leave blank to keep current)" : "Access Token (secret)"} 
              type={showToken ? 'text' : 'password'} 
              value={getCurrentForm().accessToken} 
              onChange={(e) => {
                if (editingConnection) {
                  setEditForm({ ...editForm, accessToken: e.target.value });
                } else {
                  setForm({ ...form, accessToken: e.target.value });
                }
              }} 
              fullWidth 
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle access token visibility"
                      onClick={() => setShowToken(!showToken)}
                      edge="end"
                      size="small"
                    >
                      {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />
            
            {testResponse && (
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}>
                  <Typography variant="subtitle2" color={testResponse.success ? 'success.main' : 'error.main'}>
                    Test Response ({testResponse.success ? 'Success' : 'Error'}) - Status: {testResponse.status}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails>
                  {testResponse.message && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: testResponse.success ? 'success.light' : 'error.light', borderRadius: 1 }}>
                      <Typography variant="body2" color={testResponse.success ? 'success.dark' : 'error.dark'}>
                        {testResponse.message}
                      </Typography>
                    </Box>
                  )}
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                      Raw Response Data:
                    </Typography>
                    <pre style={{ margin: 0, fontSize: '12px', whiteSpace: 'pre-wrap' }}>
                      {JSON.stringify(testResponse, null, 2)}
                    </pre>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={()=>setDialogOpen(false)}>Cancel</Button>
          <Button 
            variant="outlined" 
            onClick={testConnection} 
            disabled={testing || !getCurrentForm().label || (!getCurrentForm().accessToken && !editingConnection)}
          >
            {testing ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button 
            variant="contained" 
            onClick={saveConnection}
            disabled={saving || (testResponse && !testResponse.success) || (!editingConnection && (!testResponse || !testResponse.success))}
            color={testResponse && !testResponse.success ? 'error' : 'primary'}
          >
            {saving ? 'Saving...' : (editingConnection ? 'Update' : 'Save')}
            {testResponse && !testResponse.success && ' (Test Failed)'}
            {!editingConnection && (!testResponse || !testResponse.success) && ' (Test Required)'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Delete Samsara Connection</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the connection "{connectionToDelete?.label}"? 
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={confirmDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}


